import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import generateQueryKey from '../helpers/generate-query-key';
import { generateUrl } from '../../helpers/request-builder';
import { CubedConfigResource, CubedResource } from '../../types';

type RequestResponseData = { [key: string]: string | number | boolean };

type UsePostMultiResourcesWithPayloadArgs = {
    resource: CubedResource | CubedConfigResource;
    handleOnSuccess?: () => void;
    handleOnError?: () => void;
};

const usePostMultiResourcesWithPayload = ({
    resource,
    handleOnSuccess,
    handleOnError,
}: UsePostMultiResourcesWithPayloadArgs) => {
    const queryClient = useQueryClient();
    const queryKey = generateQueryKey({ resource });

    const mutation = useMutation({
        mutationFn: async (payload: RequestResponseData[]) => {
            const requests = payload.map(resourceData => {
                const url = generateUrl(resource.category, resource.id);

                if (url) {
                    return axios({
                        method: 'POST',
                        url: url,
                        data: resourceData,
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }).catch(error => {
                        throw new Error(error.response.data.message);
                    });
                }
                throw new Error('Error when generating url');
            });

            await Promise.all(requests);
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: queryKey,
                })
                .then(() => {
                    handleOnSuccess && handleOnSuccess();
                });
        },
        onError: () => {
            queryClient
                .invalidateQueries({
                    queryKey: queryKey,
                })
                .then(() => {
                    handleOnError && handleOnError();
                });
        },
    });

    return mutation;
};

export default usePostMultiResourcesWithPayload;
